import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { map, Observable, of, switchMap } from 'rxjs';
import { EventModel } from '../models/event.model';
import { SectorModel } from '../models/sector.model';
import { TicketModel } from '../models/ticket.model';
import { PdvRepository } from '../repositories/pdv.repository';
import { EventsRepository } from '../repositories/events.repository';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { GeneralHelper } from '../helpers/general.helper';
import { select, Store } from '@ngrx/store';
import { EventSelectors, PdvSelectors } from '../store/pdv/selectors';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  constructor(
    private pdvRepository: PdvRepository,
    private sanitizer: DomSanitizer,
    private eventsRepository: EventsRepository,
    private generalHelper: GeneralHelper,
    private http: HttpClient,
    private store: Store
  ) { }

  public groupByCategories(params?: any): Observable<EventModel[]> {
    let events$: Observable<any>;

    if (params?.comissionerId) {
      events$ = this.pdvRepository.loadSite(params)
        .pipe(
          map((res) => {
            return res.events.map((res2) => {
              return this.treatHomeEventList(res2)
            })
          })
        );
    } else {
      events$ = this.store.pipe(
        select(EventSelectors.selectFilteredEvents),
        switchMap((filteredEvents) => {
          if (filteredEvents === null) {
            return this.getSiteEvents();
          }
          return of(filteredEvents);
        })
      );
    }

    return events$.pipe(
      map((events) => [...events])
    );
  }

  public getCupomSales(eventId: string, cupom: string) {
    return this.pdvRepository.cupomSalesReport(eventId, cupom);
  }

  public groupByMonths(params?: any): Observable<EventModel[]> {
    let events$: Observable<any>;

    if (params?.comissionerId) {
      events$ = this.pdvRepository.loadSite(params).pipe(
        map((res) => {
          return res.events.map((res2) => {
            return this.treatHomeEventList(res2)
          })
        })
      );
    } else {
      events$ = this.store.pipe(
        select(EventSelectors.selectFilteredEvents),
        switchMap((filteredEvents) => {
          if (filteredEvents === null) {
            return this.getSiteEvents();
          }
          return of(filteredEvents);
        })
      );
    }

    return events$.pipe(
      map((events) => [...events])
    );
  }

  public getCustomerBuyedTickets(eventId: string) {
    return this.pdvRepository.getCustomerTickets(eventId)
  }

  public find(params: any): Observable<EventModel> {
    return this.eventsRepository.list2site(params)
      .pipe(map(res => {
        const event = res.events[0];

        const dates: string[] = event.dates.map((x: any) => x.dateStartEvent);

        let dateEnd: string | null = event.dates[0]?.dateEndEvent ?? null;

        const startDate: string = formatDate(dates[0], 'EEEE, d \'de\' MMMM', 'pt-BR', '+0000');
        const startTime: string = formatDate(dates[0], 'HH:mm', 'pt-BR', '+0000');

        if (dateEnd != null && (new Date(dateEnd as string) as unknown as number) - (new Date(dates[0]) as unknown as number) <= 32340000) {
          dateEnd = null
        }

        const googleMapsUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(encodeURI(`https://maps.google.com/maps?q=${event.address}&z=15&ie=UTF8&iwloc=&output=embed`));

        const sectors: SectorModel[] = event.sectors.map((x: any) => {
          return {
            id: x.id,
            name: x.name,
            showNoTickets: x.showNoTickets ?? null,
            position: x.position,
            useMapPlace: x.useMapPlace,
            useSeats: x.useSeats
          };
        });

        const tickets: TicketModel[] = event.sectors.flatMap((x: any) => x.tickets.map((y: any) => {

          return {
            id: y.id,
            ticketDateId: y.dateTickets[0].id,
            lotId: y.dateTickets[0].lotCurrent.id,
            fees: y.dateTickets[0].fee,
            pdvs: y.PDVs,
            sectorId: x.id,
            name: y.name,
            fengPlansExtra: y.fengPlansExtra,
            fengPlansInvited: y.fengPlansInvited,
            fengPlansPartner: y.fengPlansPartner,
            gender: y.gender,
            type: y.type,
            description: y.description,
            position: y.position,
            batch: y.dateTickets[0].lotCurrent.name,
            price: y.dateTickets[0].lotCurrent.prices[0].price,
            fee: y.dateTickets[0].lotCurrent.prices[0].priceVirtual - y.dateTickets[0].lotCurrent.prices[0].price,
            qty: 0,
            blacklist: y.blacklist,
            useFacialRecognition: y.useFacialRecognition,
            shouldHideTypeOnFrontend: y.shouldHideTypeOnFrontend
          };
        }));

        return {
          id: event.id,
          name: event.name,
          description: event.description,
          banner: event.imageWeb,
          address: event.address,
          showCupom: event.showCupom,
          city: event.city.name,
          fbPixel: event.fbPixel,
          categories: event.categories,
          hideFullTickets: event.hideFullTickets,
          state: event.city.state.name,
          uf: event.city.state.uf,
          integrations: event.integrations,
          instagram: event.instagram,
          paymentMethods: event.paymentMethods,
          limitCPF: event?.company.limitPerCPF,
          limitByCPF: event?.limitByCPF,
          dates: dates,
          dateEnd: dateEnd,
          seatsioEventKey: event.seatsioEventKey,
          seatsioMapKey: event.seatsioMapKey,
          useGoogleEvents: event.useGoogleEvents,
          googleAnalyticsKey: event.googleAnalyticsKey,
          googleTagManagerKey: event.googleTagManagerKey,
          startDate: startDate,
          startTime: startTime,
          googleMapsUrl: googleMapsUrl,
          sectors: ["c6404a91-7340-408a-a170-e56c381efae8", "2a9862d4-efd4-4104-be26-0d29f0900f5e", "5c353325-cd80-4bdb-9132-2d44885fb172", "b501cbf7-0351-452e-a6ca-c0db99d73fba", "16e20386-8ad9-41cc-b09a-ce58b1ac2192", "91257ab5-acd3-4a1f-ac2f-46d1e3bcd190", "0fa70c2d-246c-4561-b217-d3958963b10a", "b6122d1a-30a5-4c7a-8d2b-f75efed20d49", "d085263e-0417-4d32-8832-b55fa9a6167a"].includes(event.id) ? [] : sectors,
          tickets: ["c6404a91-7340-408a-a170-e56c381efae8", "2a9862d4-efd4-4104-be26-0d29f0900f5e", "5c353325-cd80-4bdb-9132-2d44885fb172", "b501cbf7-0351-452e-a6ca-c0db99d73fba", "16e20386-8ad9-41cc-b09a-ce58b1ac2192", "91257ab5-acd3-4a1f-ac2f-46d1e3bcd190", "0fa70c2d-246c-4561-b217-d3958963b10a", "b6122d1a-30a5-4c7a-8d2b-f75efed20d49", "d085263e-0417-4d32-8832-b55fa9a6167a"].includes(event.id) ? [] : tickets,
          slugs: event.slugs,
          allowMapV2: event.allowMapV2,
          skipCustomerDataOnPdv: event.skipCustomerDataOnPdv,
          requireCustomerDataOnPdv: event.requireCustomerDataOnPdv,
          allowInsurance: event.allowInsurance,
          allowProcessingFee: event.allowProcessingFee,
          limitPlotVirtual: event.limitPlotVirtual,
        };
      }));
  }

  loadTermsOfUse() {
    return this.pdvRepository.loadTermsOfUse();
  }

  loadAboutUs() {
    return this.pdvRepository.loadAboutUs();
  }

  getSiteEvents() {
    return this.store.pipe(select(PdvSelectors.selectEvents))
  }

  getProducerEvents(producerId?: any) {
    return this.pdvRepository.loadProducerEvents(producerId);
  }

  getProducerReportEvents(producerId?: any) {
    return this.pdvRepository.loadProducerReportEvents(producerId);
  }

  getCupomTicket(params: any): Observable<EventModel> {
    return this.eventsRepository.list2site(params).pipe(map(res => {
      const event = res.events[0];

      const dates: string[] = event.dates.map((x: any) => x.dateStartEvent);
      let dateEnd: string | null = event.dates[0]?.dateEndEvent ?? null;

      const startDate: string = formatDate(dates[0], 'EEEE, d \'de\' MMMM', 'pt-BR', '+0000');
      const startTime: string = formatDate(dates[0], 'HH:mm', 'pt-BR', '+0000');

      if (dateEnd != null && (new Date(dateEnd as string) as unknown as number) - (new Date(dates[0]) as unknown as number) <= 32340000) {
        dateEnd = null
      }

      const googleMapsUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(encodeURI(`https://maps.google.com/maps?q=${event.address}&z=15&ie=UTF8&iwloc=&output=embed`));

      const sectors: SectorModel[] = event.sectors.map((x: any) => {
        return {
          id: x.id,
          name: x.name,
          position: x.position,
          useMapPlace: x.useMapPlace,
          useSeats: x.useSeats
        };
      });

      const tickets: TicketModel[] = event.sectors.flatMap((x: any) => x.tickets.map((y: any) => {

        return {
          id: y.id,
          ticketDateId: y.dateTickets[0].id,
          lotId: y.dateTickets[0].lotCurrent.id,
          fees: y.dateTickets[0].fee,
          sectorId: x.id,
          name: y.name,
          type: y.type,
          gender: y.gender,
          ticketTagsQuantity: typeof y.tags !== 'undefined' && y.tags.length >= 1 ? y.tags[0].quantity : 0,
          description: y.description,
          position: y.position,
          batch: y.dateTickets[0].lotCurrent.name,
          price: y.dateTickets[0].lotCurrent.prices[0].price,
          fee: y.dateTickets[0].lotCurrent.prices[0].priceVirtual - y.dateTickets[0].lotCurrent.prices[0].price,
          qty: 0,
          shouldHideTypeOnFrontend: y.shouldHideTypeOnFrontend
        };
      }));

      return {
        id: event.id,
        name: event.name,
        description: event.description,
        banner: event.imageWeb,
        address: event.address,
        city: event.city.name,
        state: event.city.state.name,
        uf: event.city.state.uf,
        paymentMethods: event.paymentMethods,
        limitCPF: event?.company.limitPerCPF,
        dates: dates,
        seatsioEventKey: event.seatsioEventKey,
        seatsioMapKey: event.seatsioMapKey,
        startDate: startDate,
        dateEnd: dateEnd,
        startTime: startTime,
        googleMapsUrl: googleMapsUrl,
        sectors: sectors,
        tickets: tickets,
        slugs: event.slugs,
        limitPlotVirtual: event.limitPlotVirtual,
        allowInsurance: event.allowInsurance,
        allowProcessingFee: event.allowProcessingFee,
      };
    }));
  }

  list2site(params: any) {
    return this.eventsRepository.list2site(params).pipe(map(res => {
      const event = res.events[0];

      const dates: string[] = event.dates.map((x: any) => x.dateStartEvent);

      const startDate: string = formatDate(dates[0], 'EEEE, d \'de\' MMMM', 'pt-BR', '+0000');
      const startTime: string = formatDate(dates[0], 'HH:mm', 'pt-BR', '+0000');

      const googleMapsUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(encodeURI(`https://maps.google.com/maps?q=${event.address}&z=15&ie=UTF8&iwloc=&output=embed`));

      const sectors: SectorModel[] = event.sectors.map((x: any) => {
        return {
          id: x.id,
          name: x.name,
          position: x.position,
          useMapPlace: x.useMapPlace,
          useSeats: x.useSeats
        };
      });

      const tickets: TicketModel[] = event.sectors.flatMap((x: any) => x.tickets.map((y: any) => {

        return {
          id: y.id,
          ticketDateId: y.dateTickets[0].id,
          lotId: y.dateTickets[0].lotCurrent.id,
          fees: y.dateTickets[0].fee,
          sectorId: x.id,
          name: y.name,
          comissionerId: y.comissionerId,
          description: y.description,
          position: y.position,
          batch: y.dateTickets[0].lotCurrent.name,
          price: y.dateTickets[0].lotCurrent.prices[0].price,
          fee: y.dateTickets[0].lotCurrent.prices[0].priceVirtual - y.dateTickets[0].lotCurrent.prices[0].price,
          qty: 0
        };
      }));

      return {
        id: event.id,
        name: event.name,
        description: event.description,
        banner: event.imageWeb,
        imagePdv: event.imagePdv,
        address: event.address,
        city: event.city.name,
        state: event.city.state.name,
        uf: event.city.state.uf,
        paymentMethods: event.paymentMethods,
        limitCPF: event?.company.limitPerCPF,
        dates: dates,
        seatsioEventKey: event.seatsioEventKey,
        seatsioMapKey: event.seatsioMapKey,
        startDate: startDate,
        startTime: startTime,
        googleMapsUrl: googleMapsUrl,
        sectors: sectors,
        tickets: tickets,
        slugs: event.slugs,
        redirectToExternalLink: event.redirectToExternalLink,
        requireCustomerDataOnPdv: event.requireCustomerDataOnPdv,
        skipCustomerDataOnPdv: event.skipCustomerDataOnPdv,
        allowInsurance: event.allowInsurance,
        allowProcessingFee: event.allowProcessingFee,
        limitPlotVirtual: event.limitPlotVirtual,
      };
    }));
  }

  getMap(params: any): Observable<any> {
    return this.eventsRepository.getMap(params);
  }

  loadEventOrder(id: any): Observable<any> {
    return this.eventsRepository.getOrders(id);
  }

  list2list(params: any) {
    let query = this.generalHelper.buildQuery(params);
    return this.http.get(environment.apiUrl + '/event/list2list' + '?' + query);
  }

  treatHomeEventList(event) {
    const dates: string[] = event.dates.map((x: any) => {
      return x.dateStartEvent
    });
    const startDate: string = formatDate(dates[0], 'EEEE, d \'de\' MMMM', 'pt-BR', '+0000');
    const startTime: string = formatDate(dates[0], 'HH:mm', 'pt-BR', '+0000');
    const googleMapsUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(encodeURI(`https://maps.google.com/maps?q=${event.address}&z=15&ie=UTF8&iwloc=&output=embed`));

    const slugs =
      event?.slugs
        ?.filter((item: any) => item?.slug)
        ?.map((item: any) => ({ slug: item.slug })) ?? undefined;

    const tags =
      event?.tags
        ?.filter((item: any) => item?.tag)
        ?.map((item: any) => ({ tag: item.tag, logo: item.logo })) ?? undefined;

    const categories =
      event?.categories
        ?.filter((item: any) => item?.name)
        ?.map((item: any) => item.name) ?? undefined;

    let tagsFilter = ""
    if (event.tagsFilter != null) {
      tagsFilter = JSON.parse(event.tagsFilter)
    }

    return {
      id: event?.id ?? "",
      name: event?.name ?? "",
      banner: event?.banner,
      address: event?.address ?? "",
      instagram: event?.instagram,
      city: event?.city?.name ?? "",
      state: event?.city?.state?.name ?? "",
      uf: event?.city?.state?.uf ?? "",
      dates: dates,
      hideFullTickets: event?.hideFullTickets,
      description: event?.description,
      fengIntegration: event?.fengIntegration,
      startDate: startDate,
      startTime: startTime,
      limitCPF: event?.company?.limitPerCPF,
      limitByCPF: event?.limitByCPF,
      fbPixel: event?.fbPixel,
      redirectToExternalLink: event?.redirectToExternalLink,
      requireCustomerDataOnPdv: event?.requireCustomerDataOnPdv,
      skipCustomerDataOnPdv: event?.skipCustomerDataOnPdv,
      slugs: slugs,
      tags: tags,
      tagsFilter: tagsFilter,
      categories: categories,
      ticketTags: event?.ticketTags ?? undefined,
      googleMapsUrl: googleMapsUrl,

      seatsioEventKey: event?.seatsioEventKey ?? undefined,
      seatsioMapKey: event?.seatsioMapKey ?? undefined,
      useGoogleEvents: event?.useGoogleEvents,
      paymentMethods: event?.paymentMethods,
      allowMapV2: event?.allowMapV2,
      imageWeb: event?.imageWeb ?? undefined,
      imagePDV: event?.imagePDV ?? undefined,
      company: event?.company,
    };
  }

  getCuponSellsDailyReport(eventId: string, cupom: string) {
    return this.http.get(environment.apiUrl + '/report/getDailyCuponsSellsByEvent' + '/' +  eventId + '/' + cupom);
  }
}
